'use client'

import { Heart } from 'lucide-react'

import type { FeedItem, FeedItemLikes } from '@/features/profiles/types/feed'
import { useFeedLikeToggle } from '@/features/social/hooks/useFeedLikeToggle'
import { useCurrentUser } from '@/shared/hooks/useCurrentUser'
import { cn } from '@/shared/lib/utils'
import { CosmeticAvatarComposite } from '@/shared/ui/CosmeticAvatarComposite'

function FeedLikeCaption({
  likes,
  viewerUid,
  compact
}: {
  likes: FeedItemLikes
  viewerUid: string
  compact?: boolean
}) {
  const { count, liked_by_me, preview_users } = likes
  if (count <= 0) return null

  const text = compact ? 'text-[10px] leading-relaxed' : 'text-[11px] leading-relaxed sm:text-xs'

  const first = preview_users[0]
  const firstName = first?.name?.trim()?.split(/\s+/)[0] ?? 'Alguém'

  if (count === 1) {
    if (liked_by_me && first?.uid === viewerUid) {
      return <p className={cn('m-0 min-w-0 text-[#9b9bc4]', text)}>Você curtiu.</p>
    }
    return (
      <p className={cn('m-0 min-w-0 text-[#9b9bc4]', text)}>
        Curtido por <strong className="font-semibold text-white/90">{firstName}</strong>.
      </p>
    )
  }

  if (!first) {
    return (
      <p className={cn('m-0 min-w-0 text-[#9b9bc4]', text)}>
        Curtido por <strong className="font-semibold text-white/90">{count}</strong> pessoas.
      </p>
    )
  }

  let displayName = firstName
  if (preview_users[0]?.uid === viewerUid && preview_users[1]) {
    displayName =
      preview_users[1].name?.trim()?.split(/\s+/)[0] ?? displayName
  }

  return (
    <p className={cn('m-0 min-w-0 text-[#9b9bc4]', text)}>
      Curtido por <strong className="font-semibold text-white/90">{displayName}</strong> e outros.
    </p>
  )
}

export function SocialFeedActionBlock({
  item,
  compact
}: {
  item: FeedItem
  compact?: boolean
}) {
  const { currentUser } = useCurrentUser()
  const { mutate, isPending } = useFeedLikeToggle(item)
  const likes = item.likes ?? {
    count: 0,
    liked_by_me: false,
    preview_users: []
  }

  const heartBtn = cn(
    'inline-flex shrink-0 items-center justify-center rounded-full text-[#a8a8c4] outline-none transition-colors hover:bg-white/8 hover:text-white focus-visible:ring-2 focus-visible:ring-jacaranda/80 disabled:pointer-events-none disabled:opacity-40',
    compact ? 'size-8' : 'size-9 sm:size-10'
  )

  const heartIcon = cn(
    'stroke-2',
    compact ? 'size-4' : 'size-[1.15rem] sm:size-5',
    likes.liked_by_me && 'fill-[#e8486f] text-[#e8486f]'
  )

  return (
    <div
      className={cn(
        'flex flex-wrap items-center border-t border-white/6',
        compact
          ? 'mt-1.5 gap-x-2 gap-y-2 pt-2'
          : 'mt-2 gap-x-3 gap-y-2 pt-3 sm:mt-2.5 sm:gap-x-3.5 sm:pt-3.5'
      )}
      role="group"
      aria-label="Curtidas"
      onClick={e => e.stopPropagation()}
    >
      <button
        type="button"
        className={heartBtn}
        disabled={isPending}
        aria-pressed={likes.liked_by_me}
        aria-label={likes.liked_by_me ? 'Descurtir' : 'Curtir'}
        onClick={() => mutate()}
      >
        <Heart className={heartIcon} aria-hidden />
      </button>
      {likes.count === 0 ? (
        <span
          className={cn(
            'min-w-0 text-white/35',
            compact ? 'text-[10px]' : 'text-[11px] sm:text-xs'
          )}
        >
          Seja o primeiro a curtir
        </span>
      ) : (
        <div
          className={cn(
            'flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 sm:gap-x-2.5',
            compact && 'gap-x-1.5'
          )}
        >
          <div className="flex shrink-0 items-center">
            {likes.preview_users.slice(0, 3).map((p, i) => (
              <div
                key={p.uid}
                className={cn(
                  'relative flex size-[18px] items-center justify-center overflow-hidden rounded-full ring-1 ring-[#2c2c42]',
                  i > 0 && '-ml-1.5'
                )}
                style={{ zIndex: 10 - i }}
                aria-hidden
              >
                <CosmeticAvatarComposite
                  avatar={p.avatar}
                  frame={p.frame}
                  background={p.background}
                  size="xs"
                  className="pointer-events-none origin-center scale-50"
                />
              </div>
            ))}
          </div>
          <div className="min-w-0 flex-1">
            <FeedLikeCaption
              likes={likes}
              viewerUid={currentUser?.uid ?? ''}
              compact={compact}
            />
          </div>
        </div>
      )}
    </div>
  )
}
